import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { Spinner } from '#pplx/frontend/aether/components/Spinner/index.js';
import { PLUS_MENU_ITEM_SELECTED_EVENT_NAME, plusMenuItemSelectedEvent, spacesSubmenuOpenedEvent, } from '#pplx/frontend/apps/ask/events/ask-input-plus-menu/analytics.js';
import { useAtMentionSpaces } from '#pplx/frontend/apps/ask/hooks/spaces-hooks/useAtMentionSpaces.js';
import { useCreateSpaceModal } from '#pplx/frontend/apps/ask/spaces-shared/create-space-modal-hook/useCreateSpaceModal.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { unifiedEmojiToString } from '#pplx/frontend/common/unifiedEmojiToString.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { memo, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
const SPACES_MENU_MIN_WIDTH_PX = 225;
const SPACES_MENU_MAX_HEIGHT_PX = 310;
export const AskInputPlusMenuSpaces = memo(function AskInputPlusMenuSpaces({ enabled = true, submenuMaxWidth, onSpaceSelect }) {
    const [isOpen, setIsOpen] = useState(false);
    const { track } = useInstrumentation();
    const { mentionSpacesOptions, isLoading, onTrigger } = useAtMentionSpaces({
        reason: 'ask-input-plus-menu-spaces',
        enabled,
    });
    const onToggle = useCallback((open) => {
        setIsOpen(open);
        if (open) {
            track(spacesSubmenuOpenedEvent);
            onTrigger();
        }
    }, [onTrigger, track]);
    return (_jsx(DropdownMenu.Submenu, { isOpen: isOpen, onToggle: onToggle, maxHeightPx: SPACES_MENU_MAX_HEIGHT_PX, triggerElement: _jsx(DropdownMenu.SubmenuItem, { leadingAccessory: $icon('folder'), children: _jsx(FormattedMessage, { defaultMessage: "Projects", description: "Submenu item label for selecting a project to mention", id: "949979dff8" }) }), minWidthPx: SPACES_MENU_MIN_WIDTH_PX, maxWidthPx: submenuMaxWidth, children: _jsx(AskInputPlusMenuSpacesContent, { spaces: mentionSpacesOptions, isLoading: isLoading, onSpaceSelect: onSpaceSelect }) }));
});
export const AskInputPlusMenuSpacesContent = memo(function AskInputPlusMenuSpacesContent({ spaces, isLoading, onSpaceSelect }) {
    const { status } = useSession();
    const { track } = useInstrumentation();
    const { openCreateSpaceModal } = useCreateSpaceModal();
    const { device: { isWindowsApp }, } = useWebPlatform();
    const canCreateProject = !import.meta.env.VITE_IS_SIDECAR_BUILD && !isWindowsApp;
    const onCreateProject = useCallback(() => {
        track(plusMenuItemSelectedEvent({
            name: PLUS_MENU_ITEM_SELECTED_EVENT_NAME,
            data: { item: 'create_project' },
        }));
        openCreateSpaceModal({
            entryPoint: 'composerPlusMenu',
            onCreated: (project) => onSpaceSelect(collectionToProjectOption(project)),
        });
    }, [onSpaceSelect, openCreateSpaceModal, track]);
    const onSelectSpace = useCallback((space) => {
        track(plusMenuItemSelectedEvent({
            name: PLUS_MENU_ITEM_SELECTED_EVENT_NAME,
            data: {
                item: 'space',
                value: space.uuid,
            },
        }));
        onSpaceSelect(space);
    }, [onSpaceSelect, track]);
    const projectItems = isLoading ? (_jsx(DropdownMenu.InfoItem, { children: _jsx(Spinner, { size: 16, color: "quiet" }) })) : spaces.length === 0 ? (_jsx(DropdownMenu.InfoItem, { children: _jsx(FormattedMessage, { defaultMessage: "No projects found", description: "Empty state message when no projects match the search", id: "049dc220fe" }) })) : (spaces.map((space) => (_jsx(DropdownMenu.Item, { leadingAccessory: getSpaceIconAccessory(space.icon), onSelect: () => onSelectSpace(space), textValue: space.label, children: space.label }, space.uuid))));
    return (_jsxs(_Fragment, { children: [canCreateProject && (_jsxs(_Fragment, { children: [_jsx(DropdownMenu.Item, { leadingAccessory: $icon('plus'), onSelect: onCreateProject, disabled: status !== 'authenticated', children: _jsx(FormattedMessage, { defaultMessage: "Create new project", description: "Menu item to create a project from the Ask input plus menu", id: "8706345d2e" }) }), _jsx(DropdownMenu.Separator, {})] })), projectItems] }));
});
function getSpaceIconAccessory(icon) {
    if (icon?.type === 'emoji') {
        return _jsx("span", { "aria-hidden": true, children: icon.char });
    }
    if (icon?.type === 'icon') {
        return icon.icon;
    }
    if (icon?.type === 'image') {
        return _jsx("img", { alt: "", className: "size-4 rounded-xs object-cover", src: icon.url });
    }
    return $icon('folder-filled');
}
function collectionToProjectOption(collection) {
    const unifiedEmoji = collection.appearance?.emoji ?? collection.emoji;
    const emoji = unifiedEmoji ? unifiedEmojiToString(unifiedEmoji) : null;
    return {
        uuid: collection.uuid,
        variant: 'space',
        label: collection.title,
        icon: emoji ? { type: 'emoji', char: emoji } : { type: 'icon', icon: $icon('folder') },
    };
}
//# sourceMappingURL=AskInputPlusMenuSpaces.js.map